/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 */

// The stored price is the REAL price for the car's own pricing period — show it as-is.
// PricingPeriod: 0 = Week, 1 = Day, 2 = Month.
export function priceUnit(pricingPeriod?: number): string {
  switch (pricingPeriod) {
    case 1:
      return 'day';
    case 2:
      return 'month';
    default:
      return 'week'; // 0 = Week
  }
}

export const fmtPrice = (n: number) =>
  Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 });

// Map an ISO currency code to its symbol for display. Falls back to the code itself (e.g. "AED ")
// so an unmapped currency still renders sensibly rather than a misleading "$".
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', CAD: 'CA$', AUD: 'A$', EUR: '€', GBP: '£', JPY: '¥',
  BRL: 'R$', MXN: 'MX$', CHF: 'CHF ', CNY: '¥', INR: '₹', SGD: 'S$', NZD: 'NZ$',
};
export const currencySymbol = (code?: string): string => {
  const c = (code || 'USD').trim().toUpperCase();
  return CURRENCY_SYMBOLS[c] ?? `${c} `;
};

// ── Per-period rate tiers (like HQ daily/weekly/monthly). 0 = tier not offered. ──
export interface RateTiers {
  priceDaily?: number;
  priceWeekly?: number;
  priceMonthly?: number;
}

export type RateUnit = 'day' | 'week' | 'month';

/** Non-zero tiers in day→week→month order, for display. */
export function tierList(c: RateTiers): { amount: number; unit: RateUnit }[] {
  const out: { amount: number; unit: RateUnit }[] = [];
  if (c.priceDaily && c.priceDaily > 0) out.push({ amount: c.priceDaily, unit: 'day' });
  if (c.priceWeekly && c.priceWeekly > 0) out.push({ amount: c.priceWeekly, unit: 'week' });
  if (c.priceMonthly && c.priceMonthly > 0) out.push({ amount: c.priceMonthly, unit: 'month' });
  return out;
}

/** Pick the rate tier matching a rental of `days` days (mirrors the backend's SelectRateForDuration). */
export function selectTierForDays(
  c: RateTiers,
  fallbackPrice: number,
  fallbackPeriod: number | undefined,
  days: number,
): { amount: number; periodDays: number; unit: RateUnit } {
  const d = c.priceDaily || 0, w = c.priceWeekly || 0, m = c.priceMonthly || 0;
  if (days >= 30 && m > 0) return { amount: m, periodDays: 30, unit: 'month' };
  if (days >= 7 && w > 0) return { amount: w, periodDays: 7, unit: 'week' };
  if (d > 0) return { amount: d, periodDays: 1, unit: 'day' };
  if (w > 0) return { amount: w, periodDays: 7, unit: 'week' };
  if (m > 0) return { amount: m, periodDays: 30, unit: 'month' };
  const pd = fallbackPeriod === 1 ? 1 : fallbackPeriod === 2 ? 30 : 7;
  return { amount: fallbackPrice, periodDays: pd, unit: priceUnit(fallbackPeriod) as RateUnit };
}

/** Total for `days` days at a per-period `amount`, rounding the count of periods up (min 1). */
export function tierTotal(amount: number, periodDays: number, days: number): number {
  return amount * Math.max(1, Math.ceil(days / periodDays));
}
